import { IMAGE_MOTION_OPTIONS } from "../../lib/image-motion";
import { IMAGE_POSITION_OPTIONS } from "../../lib/image-position";

const HeroFields = {
  headline: {
    type: "custom", // Note: This remains "custom" for Puck's sake, but we'll map it to string for Zod
    label: "Headline",
    metadata: {
      source: "ai",
      description:
        "Compelling, SEO-friendly headline showcasing the main value proposition (60-80 chars)",
      promptGuidance: [
        "Natural on-page H1 copy, not a meta title.",
        "Do not use separators like '|' or ':'.",
        "Avoid label-style prefixes (e.g., 'Reviews: ...').",
      ],
      guardrails: {
        intent: "heroHeadline",
        minLength: 35,
        maxLength: 90,
        requireLocation: true,
        requireService: true,
        disallowSubstrings: ["|", ":"],
        genericPhrases: ["welcome", "our services", "quality you can trust", "beautiful spaces"],
      },
      seo: true,
      key: "hero.headline",
    },
  },
  subheadline: {
    type: "custom",
    label: "Subheadline",
    metadata: {
      source: "ai",
      description: "Supporting SEO-friendly subheadline that expands on the value (100-140 chars)",
      promptGuidance: [
        "Use 1-2 concise sentences with concrete value.",
        "Support the headline with trust/credibility detail.",
      ],
      guardrails: {
        intent: "heroSubheadline",
        minLength: 60,
        maxLength: 170,
      },
      seo: true,
      key: "hero.subheadline",
    },
  },
  keywordBadge: {
    type: "text",
    label: "Keyword Badge (optional)",
    metadata: {
      source: "ai",
      description:
        "Short trust microcopy badge (2-5 words). Examples: '4.9 Star Rated', 'Licensed & Insured'. Only claims the business has confirmed.",
      promptGuidance: [
        "Trust-oriented microcopy only.",
        "Do not use search-query phrasing (e.g., 'near me') or keyword stuffing.",
        "Do not use separators like '|' or ':'.",
      ],
      seo: true,
      key: "hero.keywordBadge",
      hydration: {
        transform: "sanitizeTrustBadge",
        fallback: "businessTrustBadge",
        overridePolicy: "userWins",
      },
    },
  },
  ctaText: {
    type: "text",
    label: "Primary CTA Button Text",
    metadata: {
      source: "ai",
      description:
        "Primary CTA for form/booking (e.g., 'Get Free Quote', 'Book Now', 'Schedule Service'). NOT for phone calls - secondary CTA handles that.",
      promptGuidance: ["Use clear action language.", "Keep it short and conversion-focused."],
      key: "hero.ctaText",
    },
  },
  ctaUrl: {
    type: "text",
    label: "Primary CTA URL",
    metadata: {
      source: "manual", // Usually fixed nav structure
      key: "hero.ctaUrl",
    },
  },
  secondaryCtaText: {
    type: "text",
    label: "Secondary CTA Text (optional)",
    metadata: {
      source: "context",
      contextKey: "business.phone",
      description:
        "Phone call CTA - auto-generated from business phone (e.g., 'Call Us: (555) 123-4567')",
    },
  },
  secondaryCtaUrl: {
    type: "text",
    label: "Secondary CTA URL (optional)",
    metadata: {
      source: "manual",
    },
  },
  trustIndicators: {
    type: "array",
    label: "Trust Indicators",
    metadata: {
      source: "context", // Derived from business data (Google rating, years in business, etc.)
      description: "Trust badges based on real business attributes (3-6 items)",
      seo: true,
      key: "hero.trust_indicators",
    },
    arrayFields: {
      text: { type: "text", label: "Text" },
    },
  },
  backgroundImage: {
    type: "custom",
    label: "Background Image",
    metadata: {
      source: "context", // Auto-selected from stock images matching business type
      contextKey: "stockImages.hero",
      description: "URL for the background image",
      seo: false,
      key: "hero.background_image",
    },
  },
  logoImage: {
    type: "custom",
    label: "Featured logo",
    metadata: {
      source: "manual",
      description: "A logo under the buttons, such as \"As seen on This Old House\", an award or a certification",
      seo: false,
    },
  },
  logoImageAlt: {
    type: "text",
    label: "Logo alt text",
    metadata: {
      source: "manual",
      description: "What the logo says, for screen readers",
      seo: true,
    },
  },
  logoImageColor: {
    type: "radio",
    label: "Logo color",
    options: [
      { label: "As uploaded", value: "original" },
      { label: "White", value: "white" },
    ],
    metadata: {
      source: "manual",
      description: "White turns a one-colour logo white so it reads on a dark photo",
    },
  },
  overlay: {
    type: "select",
    label: "Overlay Style",
    options: [
      { label: "Dark", value: "dark" },
      { label: "Light", value: "light" },
      { label: "Gradient", value: "gradient" },
      { label: "Brand color", value: "brand" },
    ],
    metadata: {
      source: "manual",
    },
  },
  overlayDarkness: {
    type: "number",
    label: "Overlay darkness",
    min: 0,
    max: 100,
    step: 5,
    metadata: {
      source: "manual",
      default: 50,
      description: "How dark the overlay is over the photo. Lower shows more of the photo; higher makes the text easier to read.",
    },
  },

  textColor: {
    type: "select",
    label: "Text Color",
    options: [
      { label: "Auto (based on overlay)", value: "auto" },
      { label: "Light (white text)", value: "light" },
      { label: "Dark (theme text color)", value: "dark" },
    ],
    metadata: {
      source: "manual",
    },
  },
  layout: {
    type: "select",
    label: "Layout",
    options: [
      { label: "Centered", value: "centered" },
      { label: "Split (Text + Image)", value: "split" },
      { label: "Showcase (Full-Screen Photos)", value: "showcase" },
    ],
    metadata: {
      source: "manual",
    },
  },
  photos: {
    type: "array",
    label: "Showcase Photos",
    metadata: {
      source: "context",
      contextKey: "business.stockImages",
      description:
        "Photos the Showcase layout fills the screen with, fading between them. One photo is fine.",
      seo: true,
      key: "hero.photos",
    },
    getItemSummary: (item: { alt?: string }, index?: number) =>
      item?.alt || `Photo ${(index ?? 0) + 1}`,
    arrayFields: {
      imageUrl: {
        type: "custom",
        label: "Photo",
        metadata: {
          description: "A full-screen photo of your work",
        },
      },
      alt: {
        type: "text",
        label: "Alt Text",
        metadata: {
          description: "Describe the work shown for accessibility and SEO",
          seo: true,
        },
      },
      position: {
        type: "select",
        label: "Image Focus",
        options: [...IMAGE_POSITION_OPTIONS],
        metadata: {
          source: "manual",
          description: "Which part of the photo stays visible when the screen crops it",
        },
      },
    },
    defaultItemProps: {
      imageUrl: "",
      alt: "",
      position: "center",
    },
  },
  slideSeconds: {
    type: "number",
    label: "Seconds Per Slide",
    min: 2,
    max: 20,
    metadata: {
      source: "manual",
      description: "How long each showcase photo stays before fading to the next",
    },
  },
  imageMotion: {
    type: "radio",
    label: "Photo Motion",
    options: [...IMAGE_MOTION_OPTIONS],
    metadata: {
      source: "manual",
      description:
        "Slow drift gives the photo a living, cinematic feel — a gentle zoom and pan instead of a static image.",
    },
  },
  imagePosition: {
    type: "select",
    label: "Image Focus",
    options: [...IMAGE_POSITION_OPTIONS],
    metadata: {
      source: "manual",
      description: "Which part of the background image stays visible when the screen crops it",
    },
  },
  showSlideshowPause: {
    type: "radio",
    label: "Pause Button",
    options: [
      { label: "Show", value: "yes" },
      { label: "Hide", value: "no" },
    ],
    metadata: {
      source: "manual",
      description:
        "Showcase layout: a small pause control so a visitor can hold a photo they want to look at.",
    },
  },
  showSlideshowDots: {
    type: "radio",
    label: "Slideshow Dots",
    options: [
      { label: "Show", value: "yes" },
      { label: "Hide", value: "no" },
    ],
    metadata: {
      source: "manual",
      description:
        "Showcase layout: dot controls so visitors can flip between photos themselves.",
    },
  },
  showCopy: {
    type: "radio",
    label: "Show Headline & Buttons",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    metadata: {
      source: "manual",
      description:
        "Showcase layout: keep the headline, subheadline and buttons over the photo, or let the work stand on its own.",
    },
  },
  variant: {
    type: "select",
    label: "Hero Variant",
    options: [
      { label: "Clean", value: "clean" },
      { label: "Panel", value: "panel" },
      { label: "Proof", value: "proof" },
      { label: "Bold", value: "bold" },
    ],
    metadata: {
      source: "manual",
      description:
        "Controls visual emphasis without changing the core H1/CTA structure. Bold sets the headline in big capitals on a dark band, with the towns served above it.",
    },
  },
  heroForm: {
    type: "select",
    label: "Hero Form",
    options: [
      { label: "Lead capture", value: "lead" },
      { label: "Booking widget", value: "booking" },
      { label: "Business facts", value: "facts" },
    ],
    metadata: {
      source: "manual",
      description:
        "What the split-layout side card shows: the quick lead form, the booking request flow, or the business's facts (licensed, insured, rating, years, hours), each only when it is true.",
    },
  },
} as const;

export { HeroFields };
